import { AlertOctagon, AlertTriangle, ArrowRight, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AttentionItem } from '@/services/org-admin/analysis';

/**
 * What the admin should look at, in one place, in plain language.
 *
 * This replaces a stat tile that read "Seats at risk: 1" — a true number that named nobody and sent
 * the reader to another tab to find out who. Each line here names the subject, the figure and the
 * consequence, so the page answers "what do I do about it" rather than only "what happened".
 *
 * Deliberate restraint:
 *   - It is **absent** when there is nothing wrong, apart from one quiet confirmation line. A banner
 *     that is always present stops being read.
 *   - It is **capped**, with an overflow count. Twelve mild warnings is noise; two specific ones get
 *     acted on.
 *   - Severity is carried by an icon and the wording, never by colour alone.
 *   - It is a notice, not an alarm: a tinted rail rather than a saturated block, because an admin
 *     seeing this every cycle should not be trained to dismiss it.
 */

/** Beyond this the list becomes a wall; the charts below carry the long tail. */
const MAX_VISIBLE = 3;

export interface AttentionCalloutProps {
  items: AttentionItem[];
  /** Sends the reader to the per-member ledger to act on it. */
  onViewDetail: () => void;
  className?: string;
}

export function AttentionCallout({ items, onViewDetail, className }: AttentionCalloutProps) {
  if (items.length === 0) {
    return (
      <p className={cn('oa-meta flex items-center gap-1.5 px-1', className)}>
        <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-success-strong" aria-hidden />
        Every seat is within budget and no service is dominating spend.
      </p>
    );
  }

  const visible = items.slice(0, MAX_VISIBLE);
  const overflow = items.length - visible.length;
  const hasCritical = items.some((item) => item.severity === 'critical');
  // Someone is actively cut off — a harder stop than the rest of `critical` (over budget but still
  // working), so it gets its own, louder treatment rather than blending into the same notice.
  const hasBlocking = items.some((item) => item.blocking);

  return (
    <section
      // `role="status"`, not `role="alert"`: this is standing context the reader arrives to, not an
      // interruption. `alert` would make a screen reader talk over whatever they were doing.
      role="status"
      aria-label="Needs attention"
      className={cn(
        'rounded-[var(--oa-radius-md)] border p-3',
        hasBlocking
          ? 'border-2 border-destructive/60 bg-destructive/[0.13]'
          : hasCritical
            ? 'border-destructive/25 bg-destructive/[0.06]'
            : 'border-warning/30 bg-warning/[0.07]',
        className,
      )}
    >
      {/*
        Title and action share the top line; the messages get the full width below.
        They were previously all three in one flex row, which on a phone squeezed the message column
        to about 110px and wrapped every sentence into two-word ribbons — the most important text on
        the page was the hardest to read.
      */}
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <h3 className={cn('flex items-center gap-2 text-[13px] font-semibold', hasBlocking && 'text-[14px]')}>
          {hasBlocking ? (
            <AlertOctagon className="h-[18px] w-[18px] shrink-0 text-destructive-strong" aria-hidden />
          ) : (
            <AlertTriangle
              className={cn(
                'h-4 w-4 shrink-0',
                hasCritical ? 'text-destructive-strong' : 'text-warning-strong',
              )}
              aria-hidden
            />
          )}
          {/* Not truncated: at phone width "Needs attention now" lost its last word, and a heading
              that reads "Needs attent…" is worse than one that wraps. */}
          <span>{hasBlocking ? 'AI usage blocked' : hasCritical ? 'Needs attention now' : 'Worth a look'}</span>
        </h3>
        <button
          type="button"
          onClick={onViewDetail}
          className="oa-interactive inline-flex min-h-6 shrink-0 items-center gap-1 rounded-md px-1.5 py-1 text-[12px] font-medium text-muted-foreground hover:bg-background/60 hover:text-foreground"
        >
          Review seats
          <ArrowRight className="h-3 w-3" aria-hidden />
        </button>
      </div>

      <ul className="mt-2 space-y-1.5 pl-6">
        {visible.map((item) => (
          <li key={item.id} className="text-[13px] leading-snug text-foreground/90">
            {/* A leading marker rather than a bullet glyph, so a one-item list does not look like a
                stray bullet and severity is visible per line. `break-words` because these messages
                contain email addresses long enough to overflow a phone-width card. */}
            <span
              aria-hidden
              className={cn(
                'mr-2 inline-block h-1 w-1 -translate-y-0.5 rounded-full align-middle',
                item.severity === 'critical' ? 'bg-destructive' : 'bg-warning',
              )}
            />
            <span className="break-words">{item.message}</span>
          </li>
        ))}
        {overflow > 0 && (
          <li className="oa-meta">
            and {overflow} more {overflow === 1 ? 'item' : 'items'}
          </li>
        )}
      </ul>
    </section>
  );
}
